import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/cn';

/**
 * The shell header — `FE-04.11`, `entry-surfaces.md` D1.
 *
 * Opaque `--color-surface-raised`, not `--color-panel`: it carries
 * `--color-ink-muted` (a workspace's secondary line, a search placeholder)
 * and B1 measured that muted text needs 98% opacity, so it does not belong
 * on a translucent sheet at all. `position: sticky` because the board face
 * scrolls under it and `FE-13.3` needs a fixed reference point.
 *
 * **Contents, left to right, fixed order:** brand → workspace switcher →
 * board switcher → flexible gap → search → notifications → account. The brand
 * is this component's own; the other five are **slots** — `ReactNode` props, not
 * components this file imports — because their content belongs to other
 * blocks (`FE-06.x`/`FE-07.x` for the switcher and the account menu,
 * `B17`/`B19`/`B20`, the *finding-things* blocks, for search and
 * notifications). "The shell provides the places those go, it does not
 * build them" (`B3-BRIEF.md`, out of scope). A slot renders nothing today
 * — `RootLayout` has no session or workspace data to pass it (this chunk
 * does not fetch) — and the DOM anchor (`data-shell-slot`) only appears
 * once a caller actually supplies content, so an empty header never ships
 * dead placeholder markup.
 *
 * Nothing is ever *removed* at a narrow width — only relabelled from
 * "label + icon" to "icon + Tooltip + accessible name" — but that
 * relabelling is each slot's own component's responsibility (it owns its
 * content and therefore its own responsive behaviour); the one breakpoint
 * this file commits to is the search slot's reserved width at ≥1024px,
 * because that is space the *shell* is claiming, not the search
 * component's own concern.
 */
export interface HeaderProps {
  /** `FE-07.x` — the workspace switcher menu. */
  workspaceSwitcherSlot?: ReactNode;
  /** `FR-BOARD-004` / `FR-EXP-017` — the board switcher, beside the workspace one. */
  boardSwitcherSlot?: ReactNode;
  /** `B19`/`B20`/`B17` — the global search entry point. Inline at ≥1024px
   *  (`w-80`, the width `entry-surfaces.md` D1 reserves); a compact trigger
   *  below that is the search component's own swap. */
  searchSlot?: ReactNode;
  /** `B17` — the notifications bell. */
  notificationsSlot?: ReactNode;
  /** `FE-06.x`/`FE-07.x` — the account menu. */
  accountSlot?: ReactNode;
  className?: string;
}

export function Header({
  workspaceSwitcherSlot,
  boardSwitcherSlot,
  searchSlot,
  notificationsSlot,
  accountSlot,
  className,
}: HeaderProps) {
  return (
    <header
      role="banner"
      className={cn(
        'sticky top-0 z-30 border-b border-border bg-surface-raised',
        className,
      )}
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
        {/*
          Brand. Wordmark at ≥640px; below that a mark-only rendering with
          the same accessible name, so the link never loses its label — the
          visible text swaps, `aria-label` does not (D1's narrow-width row).
        */}
        <Link
          to="/"
          aria-label="molt"
          className="shrink-0 rounded font-display text-lg font-semibold tracking-tight text-ink"
        >
          <span aria-hidden="true" className="hidden sm:inline">
            molt
          </span>
          <span aria-hidden="true" className="sm:hidden">
            m
          </span>
        </Link>

        {workspaceSwitcherSlot ? (
          <div data-shell-slot="workspace-switcher" className="min-w-0">
            {workspaceSwitcherSlot}
          </div>
        ) : null}

        {boardSwitcherSlot ? (
          <div data-shell-slot="board-switcher" className="min-w-0">
            {boardSwitcherSlot}
          </div>
        ) : null}

        <div className="flex-1" />

        <div className="flex shrink-0 items-center gap-2">
          {searchSlot ? (
            <div data-shell-slot="search" className="w-80 max-lg:w-auto">
              {searchSlot}
            </div>
          ) : null}
          {notificationsSlot ? (
            <div data-shell-slot="notifications">{notificationsSlot}</div>
          ) : null}
          {accountSlot ? <div data-shell-slot="account">{accountSlot}</div> : null}
        </div>
      </div>
    </header>
  );
}
